import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import Header from './components/layout/Header';
import Sidebar from './components/layout/Sidebar';
import TrialBanner from './components/subscription/TrialBanner';

import Projects from './pages/Projects';
import ProjectOverview from './pages/ProjectOverview';
import Assessments from './pages/Assessments';
import MyAssessments from './pages/MyAssessments';
import KeyMessages from './pages/KeyMessages';
import Stakeholders from './pages/Stakeholders';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import EmployeeManagement from './pages/EmployeeManagement';
import GroupManagement from './pages/GroupManagement';
import Profile from './pages/Profile';
import Plans from './pages/Plans';
import Training from './pages/Training';
import SettingsAssessmentTemplates from './pages/settings/SettingsAssessmentTemplates';
import AdminSubscriptionPlans from './pages/settings/AdminSubscriptionPlans';
import SubscriptionManagement from './pages/settings/SubscriptionManagement';
import TrialManagementDashboard from './pages/admin/TrialManagementDashboard';
import GuestAssessment from './pages/GuestAssessment';
import AIAssessmentBuilder from './pages/AIAssessmentBuilder';
import ChangeCompass from './pages/ChangeCompass';
import CommunicationDashboard from './pages/CommunicationDashboard';
import CommunicationDashboardDebug from './pages/CommunicationDashboardDebug';
import ProtectedRoute from './components/ProtectedRoute';
import Homepage from './pages/Homepage';
import Register from './pages/Register';
import SignIn from './pages/SignIn';
import ResetPassword from './pages/ResetPassword';
import SetPassword from './pages/SetPassword';
import VerifyEmail from './pages/VerifyEmail';
import FreeLandingPage from './pages/FreeLandingPage';
import PowerBITest from './components/shared/PowerBITest';
import RetellAIChat from './components/RetellAIChat';
// import RetellVoiceCall from './components/RetellVoiceCall';
import './App.css';

// AppContent component that manages layout
function AppContent() {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = React.useState(false);
  const location = useLocation();
  
  // Define public pages that don't need header/sidebar
  const publicPages = ['/', '/register', '/signin', '/reset-password', '/set-password', '/verify-email', '/free'];
  const isPublicPage = publicPages.includes(location.pathname) ||
                       location.pathname.startsWith('/verify-email') ||
                       location.pathname.startsWith('/guest/assessment');
  
  // Determine if we should show header and sidebar
  const showHeader = !isPublicPage;
  const showSidebar = !isPublicPage;
  
  // Get page title based on current path
  const getPageTitle = (pathname) => {
    // Handle Change Compass routes with project context
    if (pathname.startsWith('/change-compass/')) {
      return 'Change Compass';
    }
    
    switch (pathname) {
      case '/change-compass': return 'Change Compass';
      case '/projects': return 'Projects';
      case '/assessments': return 'Assessments';
      case '/ai-assessment-builder': return 'AI Assessment Builder';
      case '/my-assessments': return 'My Assessments';
      case '/communication-dashboard': return 'Communication Dashboard';
      case '/key-messages': return 'Key Messages';
      case '/stakeholders': return 'Stakeholders';
      case '/reports': return 'Analytics Reports';
      case '/training': return 'Training Framework';
      case '/settings': return 'Settings';
      case '/settings/employees': return 'Employee Management';
      case '/settings/groups': return 'Group Management';
      case '/settings/assessment-templates': return 'Assessment Templates';
      case '/settings/plan-management': return 'Subscription Plans';
      case '/settings/admin-subscription-plans': return 'Subscription Plans Management';
      case '/profile': return 'My Profile';
      case '/plans': return 'Communication Plans';
      case '/powerbi-test': return 'PowerBI Debug Tool';
      default: return null; // Return null for project routes if switch case misses
    }
  };

  const pageTitle = getPageTitle(location.pathname);
  const isHomePage = location.pathname === '/';

  // Handle mobile sidebar toggle
  const handleToggleMobileSidebar = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  // Close mobile sidebar when location changes
  React.useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname]);

  console.log('[AppContent] Current Path:', location.pathname);
  console.log('[AppContent] showSidebar evaluation:', showSidebar);
  const mainContentClass = `app-content ${showSidebar ? 'content-with-sidebar' : 'content-full-width'}`;
  console.log('[AppContent] main.app-content className:', mainContentClass);

  return (
    <div className="app">
      {showHeader && (
        <>
          <Header 
            pageTitle={pageTitle} 
            sidebarPresent={showSidebar}
            onToggleMobileSidebar={handleToggleMobileSidebar}
          />
          <TrialBanner />
        </>
      )}
      <div className="app-body">
        {showSidebar && (
          <Sidebar 
            isOpen={isMobileSidebarOpen}
            onClose={() => setIsMobileSidebarOpen(false)}
          />
        )}
        <main 
          className={mainContentClass}
          style={isHomePage ? { padding: 0 } : {}}
        >
          <Routes>
            <Route path="/" element={<Homepage />} />
            <Route path="/free" element={<FreeLandingPage />} />
            <Route path="/register" element={<Register />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            
            {/* Team Member Routes */}
            <Route path="/my-assessments" element={
              <ProtectedRoute allowedRoles={['Team Member', 'Change Manager', 'Admin']}>
                <MyAssessments />
              </ProtectedRoute>
            } />

            {/* Change Manager Routes */}
            <Route path="/projects" element={
              <ProtectedRoute allowedRoles={['Change Manager', 'Admin']}>
                <Projects />
              </ProtectedRoute>
            } />
            <Route path="/projects/:projectId" element={
              <ProtectedRoute allowedRoles={['Change Manager', 'Admin']}>
                <ProjectOverview />
              </ProtectedRoute>
            } />
            <Route path="/change-compass" element={
              <ProtectedRoute allowedRoles={['Change Manager', 'Admin']}>
                <ChangeCompass />
              </ProtectedRoute>
            } />
            <Route path="/change-compass/:projectId" element={
              <ProtectedRoute allowedRoles={['Change Manager', 'Admin']}>
                <ChangeCompass />
              </ProtectedRoute>
            } />
            <Route path="/assessments" element={
              <ProtectedRoute allowedRoles={['Change Manager', 'Admin']}>
                <Assessments />
              </ProtectedRoute>
            } />
            <Route path="/ai-assessment-builder" element={
              <ProtectedRoute allowedRoles={['Change Manager', 'Admin']}>
                <AIAssessmentBuilder />
              </ProtectedRoute>
            } />
            <Route path="/plans" element={
              <ProtectedRoute allowedRoles={['Change Manager', 'Admin']}>
                <Plans />
              </ProtectedRoute>
            } />
            <Route path="/stakeholders" element={
              <ProtectedRoute allowedRoles={['Change Manager', 'Admin']}>
                <Stakeholders />
              </ProtectedRoute>
            } />
            <Route path="/reports" element={
              <ProtectedRoute allowedRoles={['Change Manager', 'Admin']}>
                <Reports />
              </ProtectedRoute>
            } />
            <Route path="/training" element={
              <ProtectedRoute allowedRoles={['Change Manager', 'Admin']}>
                <Training />
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute allowedRoles={['Change Manager', 'Admin']}>
                <Settings />
              </ProtectedRoute>
            } />
            <Route path="/settings/employees" element={
              <ProtectedRoute allowedRoles={['Change Manager', 'Admin']}>
                <EmployeeManagement />
              </ProtectedRoute>
            } />
            <Route path="/settings/groups" element={
              <ProtectedRoute allowedRoles={['Change Manager', 'Admin']}>
                <GroupManagement />
              </ProtectedRoute>
            } />
            <Route path="/settings/assessment-templates" element={
              <ProtectedRoute allowedRoles={['Change Manager', 'Admin']}>
                <SettingsAssessmentTemplates />
              </ProtectedRoute>
            } />
            <Route path="/settings/plan-management" element={
              <ProtectedRoute allowedRoles={['Admin']}>
                <AdminSubscriptionPlans />
              </ProtectedRoute>
            } />
            <Route path="/settings/subscription" element={
              <ProtectedRoute allowedRoles={['Change Manager', 'Admin']}>
                <SubscriptionManagement />
              </ProtectedRoute>
            } />
            <Route path="/settings/trial-management" element={
              <ProtectedRoute allowedRoles={['Admin']}>
                <TrialManagementDashboard />
              </ProtectedRoute>
            } />
            <Route path="/guest/assessment/:invitationToken" element={<GuestAssessment />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/communication-dashboard" element={
              <ProtectedRoute allowedRoles={['Change Manager', 'Admin']}>
                <CommunicationDashboard />
              </ProtectedRoute>
            } />
            <Route path="/communication-dashboard-debug" element={
              <ProtectedRoute allowedRoles={['Change Manager', 'Admin']}>
                <CommunicationDashboardDebug />
              </ProtectedRoute>
            } />
            <Route path="/key-messages" element={
              <ProtectedRoute allowedRoles={['Change Manager', 'Admin']}>
                <KeyMessages />
              </ProtectedRoute>
            } />
            <Route path="/powerbi-test" element={
              <ProtectedRoute allowedRoles={['Change Manager', 'Admin']}>
                <PowerBITest />
              </ProtectedRoute>
            } />
          </Routes>
        </main>
      </div>
      
      {/* Retell AI Components - Only show on authenticated pages */}
      {showSidebar && (
        <>
          {/* Option 1: Chat Widget (text-based) */}
          <RetellAIChat />
          
          {/* Option 2: Voice Call Button (click-to-talk) */}
          {/* <RetellVoiceCall /> */}
        </>
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;

